import * as THREE from 'three';

export type AmbientKind = 'pollen' | 'embers' | 'snow' | 'motes' | 'ash' | 'rift' | 'none';

interface AmbientStyle {
  /** Base drift velocity (m/s) and per-particle random velocity spread. */
  vel: [number, number, number];
  spread: [number, number, number];
  /** Sway amplitude (m) and frequency. */
  sway: number;
  swayHz: number;
  size: [number, number];
  colorA: THREE.Color;
  colorB: THREE.Color;
  /** Additive (glowing) or alpha-blended (flakes). */
  additive: boolean;
  alpha: number;
  /** Flicker speed (0 = steady). */
  flicker: number;
  /** Box around the camera focus (m): half extents x/z and height. */
  box: [number, number];
  /** Share of the preset's particle budget. */
  share: number;
}

const STYLES: Record<Exclude<AmbientKind, 'none'>, AmbientStyle> = {
  pollen: {
    vel: [0.25, 0.05, 0.1], spread: [0.25, 0.12, 0.25], sway: 0.35, swayHz: 0.35, size: [0.05, 0.1],
    colorA: new THREE.Color(1.6, 1.5, 0.9), colorB: new THREE.Color(1.2, 1.4, 0.8), additive: true, alpha: 0.55, flicker: 0.6, box: [26, 9], share: 0.6,
  },
  embers: {
    vel: [0.2, 0.9, 0.05], spread: [0.4, 0.5, 0.4], sway: 0.5, swayHz: 0.7, size: [0.04, 0.09],
    colorA: new THREE.Color(3.2, 1.2, 0.3), colorB: new THREE.Color(2.4, 0.5, 0.12), additive: true, alpha: 0.9, flicker: 3.5, box: [26, 12], share: 0.8,
  },
  snow: {
    vel: [0.35, -1.1, 0.15], spread: [0.3, 0.35, 0.3], sway: 0.6, swayHz: 0.45, size: [0.05, 0.11],
    colorA: new THREE.Color(1.0, 1.02, 1.08), colorB: new THREE.Color(0.85, 0.92, 1.0), additive: false, alpha: 0.85, flicker: 0, box: [26, 14], share: 1,
  },
  motes: {
    vel: [0.08, 0.06, 0.04], spread: [0.15, 0.12, 0.15], sway: 0.4, swayHz: 0.25, size: [0.04, 0.08],
    colorA: new THREE.Color(1.3, 1.15, 0.85), colorB: new THREE.Color(0.8, 1.0, 1.3), additive: true, alpha: 0.45, flicker: 0.8, box: [24, 8], share: 0.5,
  },
  ash: {
    vel: [0.3, -0.35, 0.1], spread: [0.25, 0.2, 0.25], sway: 0.5, swayHz: 0.3, size: [0.05, 0.1],
    colorA: new THREE.Color(0.42, 0.4, 0.38), colorB: new THREE.Color(0.25, 0.23, 0.22), additive: false, alpha: 0.7, flicker: 0, box: [24, 12], share: 0.7,
  },
  rift: {
    vel: [0.0, 0.55, 0.0], spread: [0.25, 0.35, 0.25], sway: 0.45, swayHz: 0.5, size: [0.04, 0.1],
    colorA: new THREE.Color(2.4, 0.5, 2.2), colorB: new THREE.Color(0.9, 0.4, 2.6), additive: true, alpha: 0.8, flicker: 2.2, box: [24, 10], share: 0.8,
  },
};

const VERT = /* glsl */ `
attribute vec3 aSeed;      // 0..1 random position in the box
attribute vec4 aVar;       // velocity jitter xyz, size factor
uniform float uTime;
uniform vec3 uFocus;
uniform vec3 uBox;         // full box size
uniform vec3 uVel;
uniform vec3 uSpread;
uniform float uSway;
uniform float uSwayHz;
uniform vec2 uSize;
uniform float uFlicker;
uniform float uGround;
varying vec2 vUv;
varying float vFade;
varying float vMix;
void main() {
  vUv = uv;
  vec3 vel = uVel + (aVar.xyz * 2.0 - 1.0) * uSpread;
  vec3 p = aSeed * uBox + vel * uTime;
  float ph = aSeed.x * 37.0 + aSeed.z * 11.0;
  p.x += sin(uTime * uSwayHz * 6.2831 + ph) * uSway;
  p.z += cos(uTime * uSwayHz * 5.1 + ph * 1.3) * uSway;
  // Wrap into a box that follows the camera focus (world-fixed drift, endless field).
  vec3 lo = uFocus - vec3(uBox.x * 0.5, 0.0, uBox.z * 0.5);
  lo.y = uGround - 1.0;
  p = lo + mod(p - lo, uBox);
  vec3 rel = (p - lo) / uBox;
  // Fade at the box faces so wrapping never pops.
  vec3 edge = min(rel, 1.0 - rel);
  vFade = smoothstep(0.0, 0.12, edge.x) * smoothstep(0.0, 0.12, edge.z) * smoothstep(0.0, 0.1, edge.y);
  if (uFlicker > 0.0) vFade *= 0.55 + 0.45 * sin(uTime * uFlicker + ph * 3.0);
  vMix = aVar.w;
  vec4 mv = viewMatrix * vec4(p, 1.0);
  float size = mix(uSize.x, uSize.y, aVar.w);
  mv.xy += position.xy * size;
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
uniform vec3 uColorA;
uniform vec3 uColorB;
uniform float uAlpha;
varying vec2 vUv;
varying float vFade;
varying float vMix;
void main() {
  float r = length(vUv * 2.0 - 1.0);
  float a = (1.0 - smoothstep(0.0, 1.0, r));
  a = a * a * uAlpha * vFade;
  if (a < 0.004) discard;
  gl_FragColor = vec4(mix(uColorA, uColorB, vMix), a);
}`;

/**
 * Ambient motes of a zone (`ZoneLayout.ambient`): one instanced draw call
 * animated entirely in the vertex shader. Particles drift in world space
 * inside a box that follows the camera focus and wrap around its faces.
 */
export class AmbientParticles {
  readonly mesh: THREE.Mesh;
  private mat: THREE.ShaderMaterial;
  private geo: THREE.InstancedBufferGeometry;
  private time = 0;

  constructor(
    readonly kind: Exclude<AmbientKind, 'none'>,
    budget: number,
  ) {
    const s = STYLES[kind];
    const count = Math.max(1, Math.round(budget * s.share));
    const base = new THREE.PlaneGeometry(1, 1);
    this.geo = new THREE.InstancedBufferGeometry();
    this.geo.index = base.index;
    this.geo.setAttribute('position', base.getAttribute('position'));
    this.geo.setAttribute('uv', base.getAttribute('uv'));
    const seed = new Float32Array(count * 3);
    const vari = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      seed[i * 3] = Math.random();
      seed[i * 3 + 1] = Math.random();
      seed[i * 3 + 2] = Math.random();
      for (let k = 0; k < 4; k++) vari[i * 4 + k] = Math.random();
    }
    this.geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seed, 3));
    this.geo.setAttribute('aVar', new THREE.InstancedBufferAttribute(vari, 4));
    this.geo.instanceCount = count;
    this.mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uTime: { value: 0 },
        uFocus: { value: new THREE.Vector3() },
        uBox: { value: new THREE.Vector3(s.box[0] * 2, s.box[1], s.box[0] * 2) },
        uVel: { value: new THREE.Vector3(...s.vel) },
        uSpread: { value: new THREE.Vector3(...s.spread) },
        uSway: { value: s.sway },
        uSwayHz: { value: s.swayHz },
        uSize: { value: new THREE.Vector2(...s.size) },
        uFlicker: { value: s.flicker },
        uGround: { value: 0 },
        uColorA: { value: s.colorA.clone() },
        uColorB: { value: s.colorB.clone() },
        uAlpha: { value: s.alpha },
      },
      transparent: true,
      depthWrite: false,
      blending: s.additive ? THREE.AdditiveBlending : THREE.NormalBlending,
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 14;
    this.mesh.name = `ambient_${kind}`;
  }

  update(dt: number, focus: THREE.Vector3): void {
    this.time += dt;
    const u = this.mat.uniforms as Record<string, THREE.IUniform>;
    (u.uTime as THREE.IUniform<number>).value = this.time;
    (u.uFocus as THREE.IUniform<THREE.Vector3>).value.copy(focus);
    (u.uGround as THREE.IUniform<number>).value = focus.y;
  }

  dispose(): void {
    this.geo.dispose();
    this.mat.dispose();
  }
}

export function isAmbientKind(k: string): k is Exclude<AmbientKind, 'none'> {
  return k in STYLES;
}
